import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

export default function CancelBookingDialog({ booking, open, onOpenChange, onCancelled }) {
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [quote, setQuote] = useState(null);
    const [policy, setPolicy] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !booking) return;
        api.get("/cancellation-policy").then((r) => setPolicy(r.data.tiers || []));
        api.get(`/bookings/${booking.id}/refund-quote`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, [open, booking]);

    const confirm = async () => {
        if (!booking) return;
        setSubmitting(true);
        try {
            const res = await api.patch(`/bookings/${booking.id}/cancel`);
            const amt = formatMoney(res.data.refund_amount || 0);
            toast.success(t("cancel.success", amt));
            onCancelled?.(res.data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || t("cancel.error"));
        } finally {
            setSubmitting(false);
        }
    };

    if (!booking) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="cancel-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl flex items-center gap-3">
                        <AlertCircle size={22} className="text-terracotta" />
                        {t("cancel.title")}
                    </DialogTitle>
                </DialogHeader>

                <p className="text-sm text-muted2 mt-2">{t("cancel.chooseReason")}</p>

                <RefundQuoteCard quote={quote} formatMoney={formatMoney} t={t} />

                <div className="mt-2">
                    <div className="eyebrow mb-3">{t("cancel.policy")}</div>
                    <PolicyList policy={policy} t={t} />
                </div>

                <DialogFooter className="mt-6">
                    <button
                        onClick={() => onOpenChange(false)}
                        data-testid="cancel-keep"
                        className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2 font-semibold hover:bg-bone2"
                    >
                        {t("cancel.keep")}
                    </button>
                    <button
                        onClick={confirm}
                        disabled={submitting}
                        data-testid="cancel-confirm"
                        className="px-5 py-3 bg-terracotta text-bone text-xs uppercase tracking-widest2 font-semibold hover:bg-terracotta/90 disabled:opacity-50"
                    >
                        {submitting ? t("cancel.cancelling") : t("cancel.confirm")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RefundQuoteCard({ quote, formatMoney, t }) {
    return (
        <div className="mt-6 mb-2 border border-espresso/15 p-5 bg-bone2/40" data-testid="cancel-refund-quote">
            <div className="eyebrow mb-3">{t("cancel.refundLabel")}</div>
            <div className="flex items-baseline justify-between mb-2">
                <span className="font-serif text-3xl text-forest">{quote ? formatMoney(quote.refund_amount) : "—"}</span>
                <span className="text-xs uppercase tracking-widest2 text-espresso">{quote ? `${quote.refund_pct}%` : "—"}</span>
            </div>
            {quote && (
                <>
                    <div className="text-xs text-muted2 mb-1">{t("cancel.daysUntil", Math.max(0, quote.days_until_checkin))}</div>
                    <div className="text-xs text-espresso uppercase tracking-widest2" data-testid="cancel-active-policy">
                        {quote.policy_label_key ? t(`cancel.policyLabels.${quote.policy_label_key}`) : (quote.policy_label || "")}
                    </div>
                </>
            )}
        </div>
    );
}

function PolicyList({ policy, t }) {
    return (
        <ul className="space-y-1 text-xs text-muted2">
            {policy.map((tier) => (
                <li key={tier.refund_pct} className="flex justify-between" data-testid={`policy-tier-${tier.refund_pct}`}>
                    <span>{tier.min_days_before >= 1 ? t("cancel.daysBefore", tier.min_days_before) : t("cancel.sameWeek")}</span>
                    <span className="text-ink">{tier.label_key ? t(`cancel.policyLabels.${tier.label_key}`) : t(`cancel.policyTiers.${tier.label}`)}</span>
                </li>
            ))}
        </ul>
    );
}
